import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Col, Divider, Row } from 'antd';
import { useAtomValue, useAtom } from 'jotai';
import { fullSensitivityConfigAtom, selectedProductAtom } from '../atoms';
import { StyledInput, StyledUnit, StyledNestedConfigLabel, StyledInputWrapper } from './styled';
import { BetaTag } from '../styled';
import { getMetricDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import { useSelector } from 'react-redux';
import { aiAlerts1minPersistenceSelector, isAdminRoleSelector } from '@src/store/common/selectors';
import { checkErrors } from '../errorUtils';
import { SensitivityConfig } from '../interface';
import { prepareDefaultSensitivityConfig } from '../utils';
import { DEFAULT_SENSITIVITY_CONFIG_FOR_KPI_METRIC } from '@src/utils/aiAlerts/mapping';
import { AI_ALERT_KPI_BETA_METRIC_DB_KEYS } from '@src/components/ai-alerts/constants';

const SensitivityRow = ({
  sensitivity,
  isBeta = false,
  selectable = false,
  metricKey = undefined,
}) => {
  const selectedProduct = useAtomValue(selectedProductAtom);
  const [sensitivityDetails, setSensitivityDetails] = useAtom(fullSensitivityConfigAtom);
  const [errorMsg, setErrorMsg] = useState('');
  const is1minEnabled = useSelector(aiAlerts1minPersistenceSelector);
  // Backend wait-to-start is 2 min, so the real floor is 2, not 1 (CE-12272).
  const minVal = is1minEnabled ? 2 : 3;
  const isAdminUser = useSelector(isAdminRoleSelector);

  // Selectable rows (Beta KPI metrics + existing EI metrics) render a checkbox; their config
  // may not exist when deselected, so fall back to the passed metricKey for identity.
  const isSelectable = isBeta || selectable;
  const mKey = sensitivity?.metricKey ?? metricKey;
  const config = sensitivityDetails.find((m: SensitivityConfig) => m.metricKey === mKey);
  const isSelected = isSelectable ? !!config : true;
  // Selectable rows read live values from the atom; non-selectable rows keep the passed snapshot.
  const row = isSelectable ? config : sensitivity;

  const onChangeKey =
    ({ key }) =>
    (value) => {
      setSensitivityDetails((prev) => {
        return prev.map((sensitivityConfig: SensitivityConfig) => {
          if (sensitivityConfig.metricKey === mKey) {
            return { ...sensitivityConfig, [key]: value };
          }
          return sensitivityConfig;
        });
      });
    };

  const onToggleSelect = (e) => {
    const checked = e.target.checked;
    setSensitivityDetails((prev) => {
      if (checked) {
        // Beta metrics carry their AUTOALERT* backend key + KPI defaults; existing metrics
        // re-add the product's standard default threshold config.
        const addedConfig = isBeta
          ? {
              ...DEFAULT_SENSITIVITY_CONFIG_FOR_KPI_METRIC,
              metricKey: mKey,
              metric: AI_ALERT_KPI_BETA_METRIC_DB_KEYS[mKey],
            }
          : prepareDefaultSensitivityConfig(mKey, selectedProduct);
        return [...prev, addedConfig as SensitivityConfig];
      }
      return prev.filter((m: SensitivityConfig) => m.metricKey !== mKey);
    });
  };

  useEffect(() => {
    if (sensitivityDetails && mKey) {
      const sensitivityConfig = sensitivityDetails.find(
        (m: SensitivityConfig) => m.metricKey === mKey,
      );
      setErrorMsg(sensitivityConfig ? checkErrors(sensitivityConfig) : '');
    }
  }, [sensitivityDetails]);

  const metricName = getMetricDisplayName({
    metricKey: mKey,
    context: selectedProduct,
  });

  const metricUnit = getMetricUnitByKey({
    metricKey: mKey,
    context: selectedProduct,
  });

  return (
    <>
      <Divider orientation="left" orientationMargin="0" style={{ margin: '10px 0' }}>
        {isSelectable ? (
          <Checkbox
            checked={!!config}
            disabled={!isAdminUser}
            onChange={onToggleSelect}
            style={{ marginRight: 8 }}
          >
            {metricName}
            {isBeta ? <BetaTag>Beta</BetaTag> : null}
          </Checkbox>
        ) : (
          metricName
        )}
      </Divider>
      {isSelected ? (
        <>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledNestedConfigLabel>Persistence exceeds</StyledNestedConfigLabel>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={minVal}
                  max={60}
                  onChange={onChangeKey({ key: 'sustain' })}
                  value={row?.sustain}
                />
                <StyledUnit>min</StyledUnit>
              </StyledInputWrapper>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={minVal}
                  max={60}
                  onChange={onChangeKey({ key: 'warningSustain' })}
                  value={row?.warningSustain}
                />
                <StyledUnit>min</StyledUnit>
              </StyledInputWrapper>
            </Col>

            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={minVal}
                  max={60}
                  onChange={onChangeKey({ key: 'criticalSustain' })}
                  value={row?.criticalSustain}
                />
                <StyledUnit>min</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
          {!metricUnit ? (
            <div>
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col span={9}>
                  <StyledNestedConfigLabel>Concurrency declines</StyledNestedConfigLabel>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'sustainPcpDrop' })}
                      value={row?.sustainPcpDrop}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'warningPcpDrop' })}
                      value={row?.warningPcpDrop}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'criticalPcpDrop' })}
                      value={row?.criticalPcpDrop}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col span={9}>
                  <StyledNestedConfigLabel>Total Impacted Devices exceeds</StyledNestedConfigLabel>
                </Col>

                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={5}
                      max={1000000}
                      onChange={onChangeKey({ key: 'impactedUnique' })}
                      value={row?.impactedUnique}
                    />
                    <StyledUnit>#</StyledUnit>
                  </StyledInputWrapper>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={5}
                      max={1000000}
                      onChange={onChangeKey({ key: 'warningImpactedUnique' })}
                      value={row?.warningImpactedUnique}
                    />
                    <StyledUnit>#</StyledUnit>
                  </StyledInputWrapper>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={5}
                      max={1000000}
                      onChange={onChangeKey({ key: 'criticalImpactedUnique' })}
                      value={row?.criticalImpactedUnique}
                    />
                    <StyledUnit>#</StyledUnit>
                  </StyledInputWrapper>
                </Col>
              </Row>
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col span={9}>
                  <StyledNestedConfigLabel>
                    % Impacted Devices of Root Cause exceeds
                  </StyledNestedConfigLabel>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'impactedUniquePct' })}
                      value={row?.impactedUniquePct}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>
                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'warningImpactedUniquePct' })}
                      value={row?.warningImpactedUniquePct}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>

                <Col span={5}>
                  <StyledInputWrapper>
                    <StyledInput
                      disabled={!isAdminUser}
                      min={0}
                      max={100}
                      onChange={onChangeKey({ key: 'criticalImpactedUniquePct' })}
                      value={row?.criticalImpactedUniquePct}
                    />
                    <StyledUnit>%</StyledUnit>
                  </StyledInputWrapper>
                </Col>
              </Row>
            </div>
          )}
        </>
      ) : null}
      {errorMsg ? <Alert message={errorMsg} type={'error'} /> : null}
    </>
  );
};

export default React.memo(SensitivityRow);
